import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Apple, BadgeCheck, ShieldCheck, Star, Truck } from "lucide-react";
import { toast } from "sonner";
import { fetchCategories, fetchProducts, subscribeNewsletter } from "../lib/api";
import { useReviewSummary, formatReviewCount } from "../lib/reviews";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import Reveal from "../components/Reveal";
import ReviewsCarousel from "../components/ReviewsCarousel";
import Seo from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CATEGORY_IMAGES } from "../data/categoryImages";
import { CATEGORY_LINKS } from "../data/categories";

const GOOGLE_URL =
  "https://www.google.com/maps/place/Farmacy+District+Dispensary+%26+Weed+Delivery/data=!4m2!3m1!1s0x0:0x503e3290c531beac?sa=X&ved=1t:2428&ictx=111";
const APPLE_URL =
  "https://maps.apple.com/place?place-id=IB1AE849557E29FBF&address=1309+5th+St+NE%2C+Washington%2C+DC++20002%2C+United+States&coordinate=38.908582%2C-76.997395&name=Farmacy+District+DC&_provider=9902";

function GoogleG({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
    </svg>
  );
}

const HERO_IMG = "https://images.pexels.com/photos/36958676/pexels-photo-36958676.jpeg?auto=compress&cs=tinysrgb&w=1920";

const STEPS = [
  { n: "01", title: "Browse Products", text: "Explore premium flower, edibles, vapes and concentrates curated for DC." },
  { n: "02", title: "Add to Cart", text: "Pick your strains and sizes — your cart updates instantly." },
  { n: "03", title: "Verify & Checkout", text: "Quick 21+ ID verification, then checkout on our secure platform." },
  { n: "04", title: "Receive Your Order", text: "Fast, discreet delivery to your door in about 30–90 minutes." },
];

const AREAS = ["Wharf", "Shaw", "NoMa", "Navy Yard", "Georgetown", "Adams Morgan", "Foggy Bottom", "U Street Corridor", "Capitol Hill", "Dupont Circle", "Downtown / National Mall", "Northwest DC", "H Street NE", "Columbia Heights", "Southwest Waterfront", "Brookland"];

const REVIEWS = [
  { name: "Jojo Harris", text: "Delivery is always fast and smooth, and their customer service team is super friendly. Everything I've ordered has been great quality. Definitely one of the most reliable services out there." },
  { name: "Jamal Valentine", text: "Farmacy District has become my go-to spot in DC. The team is always welcoming, the quality is always on point, and you can tell they value consistency. Highly recommend." },
  { name: "Na H.", text: "I have ordered from Farmacy District three times and received perfect service each time. Good communication, fast pickup, and the quality is worth the drive." },
];

const FAQ_TEASER = [
  { q: "Where do you deliver in Washington, DC?", a: "We deliver throughout Washington, DC — all major neighborhoods, residential areas, hotels and short-term stays within the District. Delivery typically arrives within 30–90 minutes." },
  { q: "Do I need to be 21 to order?", a: "Yes. You must be 21 or older. Age is verified during checkout and valid ID may be requested on delivery, in full compliance with DC cannabis regulations." },
  { q: "How fast is delivery?", a: "Most orders arrive within 30–90 minutes. Our team communicates with you at every step so you always know where your order is." },
  { q: "Are THC edibles legal in DC?", a: "Yes. THC edibles are legal in Washington, DC under Initiative 71 when possessed and consumed in accordance with local law. All our products follow DC compliance standards." },
];

const BEST_SELLER_GROUPS = [
  (p) => /thicc/i.test(p.name) && /3000/.test(p.name),
  (p) => /sticky/i.test(p.name) && /2000/.test(p.name),
  (p) => /aloha/i.test(p.name) && /2\s?g/i.test(p.name),
  (p) => /boutiq/i.test(p.name) && /2\s?g/i.test(p.name),
  (p) => (p.category_slugs || []).includes("designers"),
];

export default function Home() {
  const [catCounts, setCatCounts] = useState({});
  const [featured, setFeatured] = useState([]);
  const [deals, setDeals] = useState([]);
  const [promo, setPromo] = useState(null);
  const [quickView, setQuickView] = useState(null);
  const [email, setEmail] = useState("");
  const rev = useReviewSummary();

  useEffect(() => {
    fetchCategories()
      .then((d) => {
        const counts = {};
        d.items.forEach((c) => {
          counts[c.slug] = { count: c.product_count, image: c.image };
        });
        setCatCounts(counts);
      })
      .catch(() => {});
    fetchProducts({ in_stock: true, limit: 500 })
      .then((d) => {
        const all = d.items;
        const sellable = (p) => p.image && (p.price > 0 || p.from_price > 0);
        const picks = [];
        BEST_SELLER_GROUPS.forEach((g) => {
          all
            .filter((p) => sellable(p) && g(p))
            .slice(0, 2)
            .forEach((p) => picks.push(p));
        });
        setFeatured(picks.length >= 6 ? picks : all.filter(sellable).slice(0, 10));
      })
      .catch(() => {});
    fetchProducts({ category: "special-deals", in_stock: true, limit: 12 })
      .then((d) => setDeals(d.items.filter((p) => p.price > 0).slice(0, 4)))
      .catch(() => {});
    fetchProducts({ category: "special-deals", limit: 30, sort: "newest" })
      .then((d) => {
        const withImage = d.items.filter((p) => p.image);
        const banner =
          withImage.find((p) => /two 1\/8 of designer/i.test(p.name)) ||
          withImage.find((p) => /3 quarter oz designer/i.test(p.name)) ||
          withImage.find((p) => /end of summer sale/i.test(p.name)) ||
          withImage.find((p) => /sale|promo/i.test(p.name) && p.price === 0) ||
          withImage[0];
        if (banner) setPromo(banner);
      })
      .catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list. Watch for deals and announcements.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email address.");
    }
  };

  return (
    <main data-testid="home-page">
      <Seo
        title="Farmacy District DC | Premium Cannabis Delivery in Washington, DC"
        description="Shop premium flower, pre-rolls, edibles, vapes and concentrates with fast, discreet delivery throughout Washington, DC. Initiative 71 compliant. Adults 21+."
      />

      <section className="relative flex min-h-[78vh] items-center overflow-hidden bg-brand-ink grain" data-testid="hero-section">
        <img src={HERO_IMG} alt="Washington DC skyline at dusk" className="absolute inset-0 h-full w-full object-cover opacity-70" fetchPriority="high" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
          <div className="max-w-2xl">
            <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-brand-lime" style={{ animationDelay: "0.05s" }}>
              Washington DC · Initiative 71 Compliant
            </p>
            <h1 className="mt-5 animate-fade-up font-display text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl" style={{ animationDelay: "0.15s" }}>
              Your Trusted Cannabis Dispensary in <span className="text-brand-lime">Washington, DC</span>
            </h1>
            <p className="mt-6 max-w-xl animate-fade-up text-base leading-relaxed text-neutral-300 sm:text-lg" style={{ animationDelay: "0.25s" }}>
              Shop premium flower, pre-rolls, edibles, vapes, concentrates and more — with fast, discreet delivery
              throughout Washington, DC.
            </p>
            <div className="mt-9 flex animate-fade-up flex-wrap gap-4" style={{ animationDelay: "0.35s" }}>
              <Link
                to="/shop"
                data-testid="hero-shop-now-cta"
                className="rounded-full bg-brand-lime px-9 py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
              >
                SHOP NOW
              </Link>
              <Link
                to="/category/special-deals"
                data-testid="hero-view-deals-cta"
                className="rounded-full border border-white/40 px-9 py-4 text-sm font-bold tracking-wide text-white transition-colors duration-200 hover:border-white hover:bg-white/10"
              >
                VIEW DEALS
              </Link>
            </div>
            <div className="mt-12 flex animate-fade-up flex-wrap gap-x-8 gap-y-3" style={{ animationDelay: "0.45s" }}>
              {[
                { icon: Truck, label: "30–90 min delivery" },
                { icon: ShieldCheck, label: "Discreet & secure" },
                { icon: BadgeCheck, label: "21+ verified" },
                { icon: Star, label: `${rev.rating} · ${formatReviewCount(rev.count)} Google reviews` },
              ].map((t) => (
                <span key={t.label} className="flex items-center gap-2 text-xs font-semibold text-neutral-300">
                  <t.icon className="h-4 w-4 text-brand-lime" /> {t.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <a
        href={GOOGLE_URL}
        target="_blank"
        rel="noreferrer"
        className="group block overflow-hidden border-y border-white/10 bg-brand-ink py-3"
        data-testid="google-reviews-bar"
        aria-label={`${rev.rating} stars from ${formatReviewCount(rev.count)} Google Reviews`}
      >
        <div className="flex w-max animate-marquee items-center gap-12">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="flex items-center gap-2.5" aria-hidden={i > 0}>
              <GoogleG className="h-4 w-4" />
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-3.5 w-3.5 fill-[#FBBC05] text-[#FBBC05] transition-transform duration-300 group-hover:scale-110" />
                ))}
              </span>
              <span className="whitespace-nowrap font-display text-sm font-black tracking-tight text-white">
                {formatReviewCount(rev.count)} Google Reviews
              </span>
            </span>
          ))}
        </div>
      </a>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28" data-testid="category-section">
        <Reveal>
          <div className="mb-10 flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">The Menu</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Shop by Category</h2>
            </div>
            <Link to="/shop" className="hidden items-center gap-1.5 text-sm font-bold text-brand-ink transition-colors hover:text-brand-red sm:flex" data-testid="category-view-all">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6" data-testid="category-list">
          {CATEGORY_LINKS.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.04}>
              <Link
                to={`/category/${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group block rounded-2xl bg-brand-sand p-6 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] sm:p-8"
              >
                <div className="flex h-32 items-center justify-center sm:h-44">
                  {(CATEGORY_IMAGES[c.slug] || catCounts[c.slug]?.image) && (
                    <img
                      src={CATEGORY_IMAGES[c.slug] || catCounts[c.slug].image}
                      alt={`${c.label} category`}
                      loading="lazy"
                      className="max-h-full w-auto object-contain transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                  )}
                </div>
                <h3 className="mt-4 font-display text-base font-black uppercase tracking-wide sm:text-lg">{c.label}</h3>
                <span className="mx-auto mt-2 block h-1 w-8 rounded-full bg-brand-lime transition-[width] duration-300 group-hover:w-12" />
                <p className="mt-2 text-[11px] font-semibold text-neutral-400">
                  {catCounts[c.slug]?.count ?? ""} {catCounts[c.slug] ? "products" : ""}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {promo && (
        <section className="bg-brand-lime" data-testid="promo-banner">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-6 px-4 py-8 sm:px-6">
            <div className="flex items-center gap-5">
              <img src={promo.image} alt={promo.name} className="h-28 w-28 flex-shrink-0 object-contain sm:h-32 sm:w-32" loading="lazy" />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-ink/70">Current Promotion</p>
                <h2 className="font-display text-2xl font-black tracking-tight text-brand-ink sm:text-3xl">{promo.name}</h2>
                <p className="text-xs font-semibold text-brand-ink/70">
                  Plus 10% off your first order with code FirstPurchase
                </p>
              </div>
            </div>
            <Link
              to={`/product/${promo.slug}`}
              data-testid="promo-banner-cta"
              className="ml-auto rounded-full bg-brand-ink px-7 py-3 text-sm font-bold tracking-wide text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            >
              SHOP THE SALE
            </Link>
          </div>
        </section>
      )}

      <section className="relative bg-brand-ink py-20 text-white grain sm:py-28" data-testid="reviews-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">
              Farmacy District, According to Customers
            </p>
            <h2 className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
              Hear it from people who shop Farmacy District.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:gap-6">
            <Reveal>
              <a
                href={GOOGLE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="google-reviews-card"
                className="block rounded-2xl bg-white p-7 text-brand-ink transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <GoogleG />
                    <span className="font-display text-xl font-black tracking-tight">Google</span>
                  </span>
                  <span className="flex items-center gap-1 text-xs font-bold text-neutral-500">
                    View profile <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
                <div className="mt-5 flex items-end gap-2.5">
                  <span className="font-display text-5xl font-black leading-none">{rev.rating}</span>
                  <span className="flex gap-0.5 pb-1" aria-label={`${rev.rating} out of 5 stars`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-brand-lime text-brand-lime" />
                    ))}
                  </span>
                  <span className="pb-1 text-sm font-semibold text-neutral-500" data-testid="google-review-count">
                    {formatReviewCount(rev.count)} reviews
                  </span>
                </div>
              </a>
            </Reveal>
            <Reveal delay={0.08}>
              <a
                href={APPLE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="apple-maps-card"
                className="block h-full rounded-2xl bg-white p-7 text-brand-ink transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.25)]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <Apple className="h-6 w-6" />
                    <span className="font-display text-xl font-black tracking-tight">Apple Maps</span>
                  </span>
                  <span className="flex items-center gap-1 text-xs font-bold text-neutral-500">
                    View profile <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
                <p className="mt-5 font-display text-2xl font-black leading-tight">Find us on Apple Maps</p>
                <p className="mt-1.5 text-sm font-semibold text-neutral-500">1309 5th St NE, Washington, DC</p>
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 text-xs font-bold uppercase tracking-[0.25em] text-neutral-400">
              Reviews you can verify on Google
            </p>
          </Reveal>
          <div className="mt-5">
            <ReviewsCarousel reviews={REVIEWS} googleUrl={GOOGLE_URL} />
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-10 flex items-end justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">Customer Favorites</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Best Sellers</h2>
              </div>
              <Link to="/shop/all" className="hidden items-center gap-1.5 text-sm font-bold transition-colors hover:text-brand-red sm:flex" data-testid="featured-view-all">
                Shop all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 gap-y-10 md:grid-cols-3 md:gap-8 lg:grid-cols-5">
            {featured.map((p, i) => (
              <Reveal key={p.product_id} delay={(i % 5) * 0.06}>
                <ProductCard product={p} onQuickView={setQuickView} />
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center sm:hidden">
            <Link to="/shop/all" className="inline-block rounded-full border border-brand-ink px-8 py-3.5 text-sm font-bold" data-testid="featured-view-all-mobile">
              SHOP ALL PRODUCTS
            </Link>
          </div>
        </div>
      </section>

      <section className="relative bg-brand-ink py-20 text-white sm:py-28 grain" data-testid="deals-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Limited Time Only</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Special Deals</h2>
                <p className="mt-3 max-w-md text-sm text-neutral-400">
                  Exclusive mix &amp; match bundles and rotating promos. First order? Use code{" "}
                  <span className="font-bold text-brand-lime">FirstPurchase</span> for 10% off.
                </p>
              </div>
              <Link
                to="/category/special-deals"
                data-testid="deals-shop-all-cta"
                className="rounded-full bg-brand-lime px-8 py-3.5 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
              >
                SHOP ALL DEALS
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 gap-y-10 lg:grid-cols-4 lg:gap-8">
            {deals.map((p, i) => (
              <Reveal key={p.product_id} delay={(i % 4) * 0.06}>
                <div className="[&_h3]:text-white [&_p]:text-neutral-400 [&_span]:text-white">
                  <ProductCard product={p} onQuickView={setQuickView} showCompare />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28" data-testid="how-to-order-section">
        <Reveal>
          <div className="mb-12 max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">Simple by Design</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">How to Order</h2>
          </div>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="group h-full border border-neutral-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]" data-testid={`order-step-${i + 1}`}>
                <span className="font-display text-4xl font-black text-brand-lime">{s.n}</span>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10">
            <Link to="/how-to-order" className="text-sm font-bold text-brand-red underline-offset-4 hover:underline" data-testid="how-to-order-link">
              See the full ordering guide
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="bg-brand-sand py-20 sm:py-28" data-testid="service-area-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">Delivery Coverage</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Delivering Across the District
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              From the Wharf to Georgetown, Navy Yard to Adams Morgan — if you're in DC, we've got you. We serve
              residents, visitors, hotels, Airbnbs and short-term stays.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span key={a} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-semibold transition-colors hover:border-brand-ink" data-testid={`area-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {a}
                </span>
              ))}
            </div>
            <Link
              to="/delivery"
              data-testid="service-area-cta"
              className="mt-9 inline-block rounded-full bg-brand-ink px-8 py-3.5 text-sm font-bold tracking-wide text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-neutral-800"
            >
              DELIVERY DETAILS
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden">
              <img
                src="https://irp.cdn-website.com/7c67d2fe/dms3rep/multi/opt/pexels-photo-14210477-1435h.jpeg"
                alt="Washington Monument illuminated at night, Washington DC"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover sm:aspect-[5/4]"
              />
              <div className="absolute bottom-4 left-4 bg-white/90 px-5 py-3 backdrop-blur">
                <p className="font-display text-sm font-black tracking-tight">All of Washington, DC</p>
                <p className="text-[11px] text-neutral-500">In compliance with local cannabis laws</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="faq-teaser-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500">Good to Know</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">FAQs</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              Everything you need to know about ordering cannabis delivery in Washington, DC.
            </p>
            <Link to="/faq" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-brand-red" data-testid="faq-view-all-link">
              View all FAQs <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <Accordion type="single" collapsible>
              {FAQ_TEASER.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-teaser-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-bold tracking-tight">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-neutral-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-brand-ink py-20 text-white grain" data-testid="newsletter-section">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Join the Mailing List</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Deals Drop First Here</h2>
            <p className="mt-3 text-sm text-neutral-400">Subscribe for updates, new drops and special announcements.</p>
            <form onSubmit={subscribe} className="mx-auto mt-8 flex max-w-md gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                aria-label="Email address"
                data-testid="newsletter-email-input"
                className="w-full rounded-full border border-white/20 bg-white/5 px-5 py-3.5 text-sm text-white outline-none placeholder:text-neutral-500 focus:border-brand-lime"
              />
              <button
                type="submit"
                data-testid="newsletter-subscribe-button"
                className="flex-shrink-0 rounded-full bg-brand-lime px-6 py-3.5 text-sm font-bold text-brand-ink transition-colors hover:bg-brand-limehover"
              >
                SIGN UP
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <QuickView slug={quickView} onClose={() => setQuickView(null)} />
    </main>
  );
}
